import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Clock, X, ExternalLink } from 'lucide-react';
import Payments from './Payments';

// Calculate remaining days based on trial end date
const calculateRemainingDays = (endDate) => {
  if (!endDate) return 0;
  
  const trialEnd = endDate instanceof Date ? endDate : endDate.toDate();
  const now = new Date();
  
  // Calculate the difference in days
  const diffTime = trialEnd - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  // Return 0 if trial has ended
  return Math.max(0, diffDays);
};

const TrialBanner = ({ onDismiss }) => {
  const { trialInfo, subscription } = useAuth();
  const t = useTranslation();
  const [showPayments, setShowPayments] = useState(false);
  
  // Calculate days remaining
  const daysRemaining = trialInfo?.isInTrial 
    ? (trialInfo.trialDaysRemaining || calculateRemainingDays(trialInfo.trialEndDate))
    : null;
  
  // Determine if user needs to restart subscription (not in trial and subscription is canceled or missing)
  const needsRestart = !trialInfo?.isInTrial && (!subscription || subscription.status === 'canceled');
  
  // Determine if trial is expired (in trial but 0 days remaining)
  const isTrialExpired = trialInfo?.isInTrial && daysRemaining === 0;
  
  // Show payments component when days remaining is 0 or needs restart
  useEffect(() => {
    if (daysRemaining === 0 || needsRestart) {
      setShowPayments(true);
    }
  }, [daysRemaining, needsRestart]);
  
  // If user needs to restart subscription, show only the modal (block app usage)
  if (needsRestart) {
    return (
      showPayments && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl">
            <Payments isRestart />
          </div>
        </div>
      )
    );
  }

  // If trial is expired, show only the modal (block app usage) - non-closable
  if (isTrialExpired) {
    return (
      showPayments && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl">
            <Payments isExpired />
          </div>
        </div>
      )
    );
  }

  // If not in trial, do not render the banner at all
  if (!trialInfo?.isInTrial) {
    return null;
  }

  return (
    <>
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-800 dark:to-indigo-800 text-white w-full">
        <div className="flex items-center justify-between px-4 py-2 relative">
          {/* Empty div for alignment */}
          <div className="w-8">
            {onDismiss && (
              <button 
                className="p-1 rounded-full hover:bg-white/20 transition-colors"
                onClick={onDismiss}
                aria-label={t('common.dismiss')}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Centered message */}
          <div className="flex items-center justify-center">
            <Clock className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-sm font-medium">
              {t('trial.banner', { days: daysRemaining })}
            </span>
            <a 
              href="#" 
              className="ml-2 text-xs sm:text-sm font-medium flex items-center hover:underline text-white"
              onClick={(e) => { 
                e.preventDefault(); 
                setShowPayments(true);
              }}
            >
              {t('trial.upgrade')}
              <ExternalLink className="w-3 h-3 ml-1" />
            </a>
          </div>
          
          {/* Empty div for alignment */}
          <div className="w-8"></div>
        </div>
      </div>

      {showPayments && !isTrialExpired && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            // Allow backdrop click to close when trial is still active
            if (e.target === e.currentTarget && daysRemaining > 0) {
              setShowPayments(false);
            }
          }}
        >
          {/* The relative container for positioning content */}
          <div className="relative w-full max-w-4xl">
            {/* Payments component now handles its own close button internally */}
            <Payments isUpgrade={daysRemaining > 0} onClose={() => setShowPayments(false)} />
          </div>
        </div>
      )}
    </>
  );
};

export default TrialBanner; 